import { forwardRef, useState } from 'react';
import Draggable from 'react-draggable';
import styled from 'styled-components';
import cx from 'classnames';
import { useMeasure, useWindowSize } from 'react-use';
import IconButton from './IconButton';
import XIcon from '../icons/XIcon';
import DragHandleIcon from '../icons/DragHandleIcon';
import ChevronIcon from '../icons/ChevronIcon';

const dragHandleClass = 'draggable-handle';

// NOTE: we're not using the reactstrap Modal component for this since we don't want to block interactions on the rest of the page.
const Modal = styled.div`
  position: absolute;

  ${({ startPosition: { top, left, right, bottom } }) =>
    [
      top ? `top: ${top}px;` : '',
      left ? `left: ${left}px;` : '',
      right ? `right: ${right}px;` : '',
      bottom ? `bottom: ${bottom}px;` : '',
    ].join('\n')}

  background: var(--side-panel-color);
  border: 0.5px solid #9c9c9c;

  z-index: 1050;

  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const Header = forwardRef(({ children }, ref) => (
  <div
    ref={ref}
    className="d-flex align-items-center justify-content-between w-100 border-bottom"
  >
    {children}
  </div>
));
Header.displayName = 'Header'; // Satisfies eslint/display-name with forwardRef

const Footer = forwardRef(({ children, className }, ref) => (
  <div ref={ref} className={`p-2 border-top w-100 ${className}`}>
    {children}
  </div>
));
Footer.displayName = 'Footer'; // Satisfies eslint/display-name with forwardRef

export const Body = styled.div`
  overflow-y: auto;
`;

const CloseButton = styled(IconButton)`
  width: 36px;
  height: 36px;
`;

const DraggableModal = ({
  className = null,
  isOpen,
  setIsOpen,
  children,
  title,
  width = 256,
  height = 360,
  startPosition = { top: 150, right: 100 },
  footerContent = null,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentRef, contentSize] = useMeasure();
  const [headerRef, headerSize] = useMeasure();
  const [footerRef, footerSize] = useMeasure();

  const headerAndFooterHeight = headerSize.height + footerSize.height;
  const maxHeight =
    useWindowSize().height - headerAndFooterHeight - startPosition.top;

  const fullHeight = Math.min(contentSize.height, maxHeight);

  const hasExpansion = fullHeight > height;
  const currentHeight = isExpanded
    ? fullHeight
    : Math.min(contentSize.height, height);

  const renderHeader = (headerContent = null) => (
    <Header ref={headerRef}>
      <div
        className={cx(
          'cursor-grab p-2 d-flex align-items-center flex-grow-1',
          dragHandleClass,
        )}
      >
        <DragHandleIcon />
        <h3 className="p-0 my-0 mr-auto ml-1">{title}</h3>
        {headerContent && headerContent}
      </div>

      <CloseButton
        color="link"
        size="sm"
        className="p-2"
        onClick={() => setIsOpen(false)}
      >
        <XIcon width={10} height={10} />
      </CloseButton>
    </Header>
  );

  const renderBody = (bodyContent = null) => (
    <div>
      <Body style={{ height: currentHeight }}>
        <div ref={contentRef}>{bodyContent}</div>
      </Body>

      {hasExpansion && (
        <div className="w-100 d-flex justify-content-center">
          <IconButton
            color="link"
            size="sm"
            onClick={() => {
              setIsExpanded((c) => !c);
            }}
          >
            <ChevronIcon direction={isExpanded ? 'up' : 'down'} />
          </IconButton>
        </div>
      )}
    </div>
  );

  const renderFooter = () =>
    footerContent && <Footer ref={footerRef}>{footerContent}</Footer>;

  return (
    <Draggable handle={`.${dragHandleClass}`} bounds="body" {...props}>
      <Modal
        isOpen={isOpen}
        className={`subtle-shadow rounded ${className}`}
        startPosition={startPosition}
        style={{ width }}
      >
        {typeof children === 'function' ? (
          children({
            headerAndFooterHeight,
            isExpanded,
            maxHeight,
            renderHeader,
            renderBody,
            renderFooter,
          })
        ) : (
          <>
            {renderHeader()}
            {renderBody(children)}
            {renderFooter()}
          </>
        )}
      </Modal>
    </Draggable>
  );
};

export default DraggableModal;
